import { useRef } from 'react';
import type { PassbookAddField } from '@/shared/types/passbook';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskFromPattern } from '@/shared/lib/input-masks';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { addressFromViaCep, fetchViaCep, zipcodeDigits } from '../model/zipcode';

interface PublicInviteCustomFieldsProps {
  fields: PassbookAddField[];
  values: Record<string, string>;
  onChange: (slug: string, value: string) => void;
}

function fieldOptions(field: PassbookAddField): string[] {
  if (Array.isArray(field.options)) return field.options.map(String);
  if (typeof field.options === 'string') return field.options.split('|');
  return [];
}

function colSpan(size: PassbookAddField['size']): number {
  const n = Number(size);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : 12;
}

/**
 * Campos customizados por loja (`passbook.addFields`) — legado renderiza via
 * loop de `fields_passbook` em quase todos os templates (brincarte, bubblemix,
 * emporiobrownie, goflashwash, abf, lavessel…). Um único componente genérico
 * cobre todos: a diferença entre essas lojas é DADO (quais campos existem),
 * não código.
 */
export function PublicInviteCustomFields({
  fields,
  values,
  onChange,
}: PublicInviteCustomFieldsProps) {
  const zipcodeRequest = useRef<AbortController | null>(null);

  if (fields.length === 0) return null;

  // integration === 'zipcode': legado deixa só dígitos (sem máscara) e, com 8,
  // busca no ViaCEP e preenche os campos de endereço que existirem no passbook.
  const changeText = (field: PassbookAddField, raw: string) => {
    if (field.integration !== 'zipcode') {
      onChange(field.slug, raw);
      return;
    }
    const value = field.mask ? raw : zipcodeDigits(raw);
    onChange(field.slug, value);

    const cep = zipcodeDigits(value);
    if (cep.length !== 8) return;
    zipcodeRequest.current?.abort();
    const request = new AbortController();
    zipcodeRequest.current = request;
    void fetchViaCep(cep, request.signal).then((data) => {
      if (!data || request.signal.aborted) return;
      const slugs = new Set(fields.map((f) => f.slug));
      for (const [slug, address] of Object.entries(addressFromViaCep(data))) {
        if (slugs.has(slug)) onChange(slug, address);
      }
    });
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      {fields.map((field) => {
        if (field.highlight) {
          return (
            <h5 key={field.slug} className="col-span-12 text-sm font-semibold">
              {field.label}
            </h5>
          );
        }

        const options = fieldOptions(field);
        const value = values[field.slug] ?? '';
        // Legado só revela o campo opcional quando a PRIMEIRA opção é selecionada
        // (checkbox pode ter várias marcadas ao mesmo tempo — testa inclusão, não igualdade).
        const firstOptionActive =
          field.type === 'checkbox'
            ? value.split('|').includes(options[0] ?? '')
            : value === options[0];
        const showOptionalField =
          Boolean(field.optionalField) && options[0] != null && firstOptionActive;

        return (
          <div
            key={field.slug}
            style={{ gridColumn: `span ${colSpan(field.size)}` }}
            className="space-y-2"
          >
            {/* Legado imprime `field.label` puro, sem `*` programático — quem quer
                o indicador de obrigatório já digita no próprio texto do label ao
                configurar o campo. Adicionar um `*` aqui duplicava em cima do que
                já vinha no texto (ex.: "Loja Favorita *" virava "Loja Favorita * *"). */}
            <Label htmlFor={field.slug}>{field.label}</Label>

            {field.type === 'select' ? (
              <Select value={value || undefined} onValueChange={(v) => onChange(field.slug, v)}>
                <SelectTrigger id={field.slug}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === 'radio' ? (
              <RadioGroup value={value} onValueChange={(v) => onChange(field.slug, v)}>
                {options.map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value={opt} id={`${field.slug}-${opt}`} />
                    {opt}
                  </label>
                ))}
              </RadioGroup>
            ) : field.type === 'checkbox' ? (
              <div className="space-y-1.5">
                {options.map((opt) => {
                  const selected = value.split('|').includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => {
                          const current = value ? value.split('|') : [];
                          const next =
                            checked === true ? [...current, opt] : current.filter((v) => v !== opt);
                          onChange(field.slug, next.join('|'));
                        }}
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            ) : field.type === 'textarea' ? (
              <Textarea
                id={field.slug}
                placeholder={field.placeholder}
                disabled={field.disabled}
                value={value}
                onChange={(e) => onChange(field.slug, e.target.value)}
              />
            ) : field.mask ? (
              <MaskedInput
                id={field.slug}
                type={field.type === 'number' ? 'text' : (field.type ?? 'text')}
                inputMode={field.type === 'tel' || field.type === 'number' ? 'numeric' : undefined}
                placeholder={field.placeholder}
                disabled={field.disabled}
                value={value}
                onValueChange={(v) => changeText(field, v)}
                mask={(v) => maskFromPattern(v, field.mask ?? '')}
              />
            ) : (
              <Input
                id={field.slug}
                type={field.type === 'tel' ? 'tel' : field.type === 'number' ? 'number' : 'text'}
                inputMode={field.integration === 'zipcode' ? 'numeric' : undefined}
                placeholder={field.placeholder}
                disabled={field.disabled}
                value={value}
                onChange={(e) => changeText(field, e.target.value)}
              />
            )}

            {showOptionalField ? (
              <Input
                aria-label={field.optionalField}
                placeholder={field.optionalField}
                value={values[`optionalField_${field.slug}`] ?? ''}
                onChange={(e) => onChange(`optionalField_${field.slug}`, e.target.value)}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
